import { ChangeEvent, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link } from "react-router-dom";
import auth from "../../fire";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import axiosClient from "../../axiosClient";
import { ClipLoader } from "react-spinners";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PageHeader from "../../components/PageHeader/PageHeader";

interface ChangeEmailValues {
  newEmail: string;
  password: string;
}

const ChangeEmail = () => {
  const [user] = useAuthState(auth);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const changeEmailSchema = Yup.object().shape({
    newEmail: Yup.string().email("Invalid email").required("Email required"),
    password: Yup.string().required("Current password required"),
  });

  if (success) {
    return (
      <>
        <PageHeader backLink="/account" pageTitle="Change Email" />
        <div className="flex flex-col py-12 px-4">
          <CheckCircleIcon
            className="h-auto w-12 mx-auto text-indigo-600"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-2xl font-bold tracking-tight text-gray-900">
            Check your new inbox
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            We've sent a confirmation link to <strong>{submittedEmail}</strong>.
            Your account email won't change until you open that link. Your
            current email stays active until then.
          </p>
          <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
            <Link to="/account" className="text-sm text-indigo-600 font-medium">
              Back to account
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader backLink="/account" pageTitle="Change Email" />
      <div className="p-4">
        <h2 className="mb-1 font-bold text-gray-900 text-xl tracking-tight">
          Change Account Email
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          We'll email a confirmation link to your new address. Your email won't
          change until you open it.
        </p>
        <Formik<ChangeEmailValues>
          initialValues={{ newEmail: "", password: "" }}
          validationSchema={changeEmailSchema}
          onSubmit={async (values, { setStatus }) => {
            setStatus("");

            if (!user || !user.email) {
              setStatus({
                error: "You must be logged in to change your email.",
              });
              return;
            }

            // Re-authenticate first: Admin-SDK calls bypass Firebase's recent-
            // login requirement, so this is the gate that proves it's really
            // the account owner requesting the change.
            try {
              const cred = EmailAuthProvider.credential(
                user.email,
                values.password,
              );
              await reauthenticateWithCredential(user, cred);
            } catch (e) {
              setStatus({ error: "Incorrect password. Please try again." });
              return;
            }

            try {
              await axiosClient.post("/api/users/request-email-change", {
                newEmail: values.newEmail,
              });
              setSubmittedEmail(values.newEmail);
              setSuccess(true);
            } catch (error) {
              const err = error as {
                response?: { data?: { message?: string } };
              };
              setStatus({
                error:
                  err.response?.data?.message ||
                  "Could not request the email change. Please try again.",
              });
            }
          }}
        >
          {({
            isSubmitting,
            status,
            setFieldValue,
            setStatus,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="newEmail"
                    className="block text-sm font-medium text-gray-700"
                  >
                    New email
                  </label>
                  <Field
                    id="newEmail"
                    name="newEmail"
                    type="email"
                    autoComplete="off"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="New email address"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("newEmail", e.target.value);
                      setStatus({ error: "" });
                    }}
                  />
                  <div className="mt-1 min-h-[1.25rem]">
                    <ErrorMessage name="newEmail">
                      {(msg) => (
                        <div className="text-sm text-red-600">*{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Current password
                  </label>
                  <div className="relative">
                    <Field
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pr-10"
                      placeholder="Current password"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("password", e.target.value);
                        setStatus({ error: "" });
                      }}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 z-10 flex items-center pr-3 mt-1"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeSlashIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      ) : (
                        <EyeIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Please enter your password to confirm your identity.
                  </p>
                  <div className="mt-1 min-h-[1.25rem]">
                    <ErrorMessage name="password">
                      {(msg) => (
                        <div className="text-sm text-red-600">*{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="mt-4 group relative flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ClipLoader
                    color="white"
                    speedMultiplier={0.6}
                    size={"1.25rem"}
                  />
                ) : (
                  "Send confirmation link"
                )}
              </button>

              {status && status.error ? (
                <div className="mt-2 text-sm text-red-600 whitespace-pre-wrap">
                  *{status.error}
                </div>
              ) : null}
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ChangeEmail;
